import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast, ToastContainer } from '../../components/shared/Toast';
import ClientPageLayout from '../../components/shared/layouts/ClientPageLayout';
import { FormSkeleton } from '../../components/shared/ClientPageSkeleton';
import {
  AccountSettings,
  PasswordSettings,
  NotificationSettings,
  PrivacySettings,
  PreferencesSettings,
  DeleteAccountSettings
} from '../../components/client/settings';
import TwoFactorAuthSettings from '../../components/shared/TwoFactorAuthSettings';
import {
  useUserProfile,
  useUpdateUserProfile,
  useUserPreferences,
  useUpdateUserPreferences,
  useNotificationSettings,
  useUpdateNotificationSettings,
  useChangePassword,
  useDeleteAccount
} from '../../services/userService';

const ClientSettings = () => {
  const { user, updateUser } = useAuth();
  const toast = useToast();
  
  // Fetch data from API
  const { data: profileData, isLoading: profileLoading, refetch: refetchProfile } = useUserProfile();
  const { data: preferencesData, isLoading: preferencesLoading, refetch: refetchPreferences } = useUserPreferences();
  const { data: notificationData, isLoading: notificationLoading, refetch: refetchNotifications } = useNotificationSettings();
  
  const isLoading = profileLoading || preferencesLoading || notificationLoading;

  // Mutations
  const updateProfileMutation = useUpdateUserProfile({
    onSuccess: async (data) => {
      // Refetch profile data to get the latest from server
      await refetchProfile();
      
      if (data?.data) {
        const updatedUser = { ...user, ...data.data };
        updateUser(updatedUser);
        
        // Update form data to reflect the changes immediately
        setFormData(prev => ({
          ...prev,
          name: data.data.name || updatedUser.name || prev.name,
          email: data.data.email || updatedUser.email || prev.email,
          phone: data.data.phone || updatedUser.phone || prev.phone
        }));
      }
      toast.success('Profile Updated', 'Your profile has been updated successfully!');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update profile';
      toast.error('Update Failed', errorMessage);
    }
  });

  const updatePreferencesMutation = useUpdateUserPreferences({
    onSuccess: async (data) => {
      // Refetch preferences to get the latest from server
      await refetchPreferences();
      
      // Update local settings state with the response data
      if (data?.data) {
        const prefs = data.data;
        const privacySettings = typeof prefs.privacy_settings === 'string' 
          ? JSON.parse(prefs.privacy_settings || '{}')
          : (prefs.privacy_settings || {});
        
        setSettings(prev => ({
          ...prev,
          preferences: {
            theme: prefs.theme || prev.preferences.theme,
            language: prefs.language || prev.preferences.language,
            timezone: prefs.timezone || prev.preferences.timezone
          },
          privacy: {
            profileVisibility: privacySettings.profile_visibility || prev.privacy.profileVisibility,
            showEmail: privacySettings.show_email !== undefined ? privacySettings.show_email : prev.privacy.showEmail,
            showPhone: privacySettings.show_phone !== undefined ? privacySettings.show_phone : prev.privacy.showPhone
          }
        }));
      }
      toast.success('Preferences Updated', 'Your preferences have been updated successfully!');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update preferences';
      toast.error('Update Failed', errorMessage);
    }
  });

  const updateNotificationMutation = useUpdateNotificationSettings({
    onSuccess: async (data) => {
      // Refetch notification settings to get the latest from server
      await refetchNotifications();
      
      // Update local settings state with the response data
      if (data?.data) {
        const notif = data.data;
        setSettings(prev => ({
          ...prev,
          notifications: {
            email: notif.email_notifications !== undefined ? notif.email_notifications : prev.notifications.email,
            push: notif.push_notifications !== undefined ? notif.push_notifications : prev.notifications.push,
            sms: notif.sms_notifications !== undefined ? notif.sms_notifications : prev.notifications.sms,
            bookingUpdates: notif.booking_updates !== undefined ? notif.booking_updates : prev.notifications.bookingUpdates,
            promotions: notif.marketing_emails !== undefined ? notif.marketing_emails : prev.notifications.promotions
          }
        }));
      }
      toast.success('Notifications Updated', 'Your notification settings have been updated successfully!');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update notification settings';
      toast.error('Update Failed', errorMessage);
    }
  });

  const changePasswordMutation = useChangePassword({
    onSuccess: () => {
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      toast.success('Password Updated', 'Your password has been changed successfully!');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update password';
      toast.error('Password Update Failed', errorMessage);
    }
  });

  const deleteAccountMutation = useDeleteAccount({
    onSuccess: () => {
      toast.success('Account Deleted', 'Your account has been deleted successfully.');
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to delete account';
      toast.error('Delete Failed', errorMessage);
    }
  });

  // Initialize settings from API data
  const [settings, setSettings] = useState({
    notifications: {
      email: true,
      push: true,
      sms: false,
      bookingUpdates: true,
      promotions: false
    },
    privacy: {
      profileVisibility: 'public',
      showEmail: false,
      showPhone: false
    },
    preferences: {
      theme: 'light',
      language: 'en',
      timezone: 'America/New_York'
    }
  });

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Update form data when user or profile data loads
  useEffect(() => {
    if (profileData?.data) {
      const profile = profileData.data;
      setFormData(prev => ({
        ...prev,
        name: profile.name || user?.name || '',
        email: profile.email || user?.email || '',
        phone: profile.phone || user?.phone || ''
      }));
    } else if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || ''
      }));
    }
  }, [user, profileData]);

  // Update settings from API data
  useEffect(() => {
    if (preferencesData?.data) {
      const prefs = preferencesData.data;
      // Parse privacy_settings if it's a JSON string
      const privacySettings = typeof prefs.privacy_settings === 'string' 
        ? JSON.parse(prefs.privacy_settings || '{}')
        : (prefs.privacy_settings || {});
      
      setSettings(prev => ({
        ...prev,
        preferences: {
          theme: prefs.theme || 'light',
          language: prefs.language || 'en',
          timezone: prefs.timezone || 'America/New_York'
        },
        privacy: {
          profileVisibility: privacySettings.profile_visibility || 'public',
          showEmail: privacySettings.show_email || false,
          showPhone: privacySettings.show_phone || false
        }
      }));
    }
  }, [preferencesData]);

  useEffect(() => {
    if (notificationData?.data) {
      const notif = notificationData.data;
      setSettings(prev => ({
        ...prev,
        notifications: {
          email: notif.email_notifications ?? true,
          push: notif.push_notifications ?? true,
          sms: notif.sms_notifications ?? false,
          bookingUpdates: notif.booking_updates ?? true,
          promotions: notif.marketing_emails ?? false
        }
      }));
    }
  }, [notificationData]);

  const handleNotificationToggle = (key) => {
    const newValue = !settings.notifications[key];
    const updatedSettings = {
      ...settings,
      notifications: {
        ...settings.notifications,
        [key]: newValue
      }
    };
    setSettings(updatedSettings);

    // Map frontend keys to backend keys
    const backendKeyMap = {
      email: 'email_notifications',
      push: 'push_notifications',
      sms: 'sms_notifications',
      bookingUpdates: 'booking_updates',
      promotions: 'marketing_emails'
    };

    const backendKey = backendKeyMap[key] || key;
    updateNotificationMutation.mutate({
      [backendKey]: newValue
    });
  };

  const handlePrivacyChange = (key, value) => {
    const updatedSettings = {
      ...settings,
      privacy: {
        ...settings.privacy,
        [key]: value
      }
    };
    setSettings(updatedSettings);

    // Map frontend keys to backend keys
    const backendKeyMap = {
      profileVisibility: 'profile_visibility',
      showEmail: 'show_email',
      showPhone: 'show_phone'
    };

    const backendKey = backendKeyMap[key] || key;
    updatePreferencesMutation.mutate({
      privacy_settings: {
        [backendKey]: value
      }
    });
  };

  const handlePreferenceChange = (key, value) => {
    const updatedSettings = {
      ...settings,
      preferences: {
        ...settings.preferences,
        [key]: value
      }
    };
    setSettings(updatedSettings);

    updatePreferencesMutation.mutate({
      [key]: value
    });
  };

  const handleSave = () => {
    if (!formData.name || !formData.name.trim()) {
      toast.error('Validation Error', 'Name is required');
      return;
    }
    
    updateProfileMutation.mutate({
      name: formData.name.trim(),
      phone: formData.phone || null
    });
  };

  const handlePasswordChange = () => {
    if (!formData.currentPassword || !formData.newPassword) {
      toast.error('Validation Error', 'Please fill in all password fields');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Validation Error', 'New passwords do not match');
      return;
    }

    changePasswordMutation.mutate({
      currentPassword: formData.currentPassword,
      newPassword: formData.newPassword,
      confirmPassword: formData.confirmPassword
    });
  };

  const handleDeleteAccount = async ({ password }) => {
    await deleteAccountMutation.mutateAsync({
      ...(password ? { password } : {}),
      reason: 'User requested account deletion',
    });
  };

  if (isLoading) {
    return (
      <>
        <ToastContainer 
          toasts={toast.toasts} 
          onRemove={toast.removeToast}
          position="top-right"
        />
        <ClientPageLayout
          title="Settings"
          subtitle="Update your profile, password, and two-factor authentication."
          guideKey="settings"
        >
          <div className="space-y-5">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-5 md:p-6"
              >
                <div className="flex items-center space-x-2 mb-3 sm:mb-4">
                  <div className="w-4 h-4 sm:w-5 sm:h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                  <div className="h-5 sm:h-6 w-24 sm:w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                </div>
                <FormSkeleton fields={3} />
              </div>
            ))}
          </div>
        </ClientPageLayout>
      </>
    );
  }

  return (
    <>
      <ToastContainer 
        toasts={toast.toasts} 
        onRemove={toast.removeToast}
        position="top-right"
      />
      <ClientPageLayout
        title="Settings"
        subtitle="Update your profile, password, two-factor authentication, and account preferences."
        guideKey="settings"
      >
        <div className="space-y-5">
        <AccountSettings
          formData={formData}
          setFormData={setFormData}
          onSave={handleSave}
        />

        <PasswordSettings
          formData={formData}
          setFormData={setFormData}
          onSave={handlePasswordChange}
          isLoading={changePasswordMutation.isPending}
        />

        <div className="rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm p-4 sm:p-5 md:p-6">
          <TwoFactorAuthSettings queryKey="client-two-factor-status" />
        </div>

        {/* <NotificationSettings
          notifications={settings.notifications}
          onToggle={handleNotificationToggle}
        />

        <PrivacySettings
          privacy={settings.privacy}
          onChange={handlePrivacyChange}
        /> */}

        {/* <PreferencesSettings
          preferences={settings.preferences}
          onChange={handlePreferenceChange}
        /> */}

        <DeleteAccountSettings
          onDelete={handleDeleteAccount}
          deletionRequiresPassword={profileData?.data?.deletion_requires_password ?? true}
        />
      </div>
    </ClientPageLayout>
    </>
  );
};

export default ClientSettings;

